import React from "react";
import {Autocomplete, TextField, Checkbox, Chip, Grid, Paper, Box, Typography} from "@mui/material";
import {CheckBoxOutlineBlank, CheckBox} from "@mui/icons-material";
import {useController} from "react-hook-form";
import FormGuide from "./FormGuide";
import PropTypes from "prop-types";
import {ALERT} from "../../resources/environment";
import {useCustomAutocompleteStyles} from "./formsStyles";
import AgentFieldHighlight from "./AgentFieldHighlight";
import {findWhere, omit} from "underscore";

const CustomAutocomplete = ({
  guide,
  required,
  margin,
  name,
  options,
  optionLabel,
  placeholder = "Add",
  textFieldLabel = false,
  title,
  defaultValue,
  allowExtended,
  selectAllValue,
  agentHighlight = false,
}) => {
  const {classes} = useCustomAutocompleteStyles({margin});
  const itemWidth = guide ? 8 : 12;
  const itemMinLength = 2;
  const itemMaxLength = allowExtended ? 100 : 30;
  const maxSpaces = allowExtended ? 15 : 1;
  const optionalDefaultValue = defaultValue ? defaultValue : [];
  const whichLabel = optionLabel || "title";

  const handleChange = (e, data, onChange) => {
    const value = e.target.value;
    if (value) {
      // custom tag set, sanitize and validate it | if dropdown option is selected value == false
      const cleanValue = value.replace(/\s+/g, " ").trim();
      // above regex replaces multiple spaces with single space
      if (cleanValue === "") {
        ALERT.error("Option cannot be empty!");
        return;
      }
      if (cleanValue.split(" ").length - 1 > maxSpaces) {
        // if # of spaces > max
        ALERT.error(`Options should only be up to ${maxSpaces + 1} words`);
        return;
      }
      if (cleanValue.length < itemMinLength || cleanValue.length > itemMaxLength) {
        ALERT.error(`Options should be ${itemMinLength} to ${itemMaxLength} characters long`);
        return;
      }
      const index = data.findIndex((ele) => ele === value);
      data[index] = {[whichLabel]: cleanValue};
    }

    if (selectAllValue && findWhere(data, {[whichLabel]: selectAllValue})) {
      onChange(options);
      return;
    }

    // no need to setValue anymore, onChange from Controller takes care of that
    onChange(data);
  };

  // This custom hook is designed to take care of the registration process.
  const {
    // see: https://react-hook-form.com/api/usecontroller
    field: {onChange, value, ...inputProps},
    fieldState: {error},
  } = useController({
    name,
    defaultValue: optionalDefaultValue,
    rules: {required: required},
  });

  return (
    <AgentFieldHighlight active={agentHighlight}>
      <span className={classes.span}>
        {title && (
          <Box my={1} mt={4}>
            <Typography variant="h5">{title}</Typography>
          </Box>
        )}
        <Grid container spacing={1}>
          <Grid item xs={itemWidth}>
            <Autocomplete
              {...inputProps}
              multiple
              fullWidth
              id={name}
              value={value ?? optionalDefaultValue}
              classes={{
                inputRoot: classes.inputRoot,
                paper: classes.paper,
              }}
              options={options ?? []}
              getOptionLabel={(option) => option?.[whichLabel] || ""}
              isOptionEqualToValue={(option, value) => {
                if (option?.[whichLabel] && value?.[whichLabel]) {
                  return option[whichLabel].toLowerCase() === value[whichLabel].toLowerCase();
                }
                return false;
              }}
              freeSolo
              disableCloseOnSelect
              onChange={(e, data) => handleChange(e, data, onChange)}
              renderOption={(props, option, {selected}) => (
                <li key={props?.key} {...omit(props, "key")}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBox fontSize="small" />}
                    style={{marginRight: 8}}
                    checked={selected}
                    color="secondary"
                  />
                  {option[whichLabel]}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  return (
                    <Chip
                      variant="outlined"
                      margin="normal"
                      key={index}
                      label={option?.[whichLabel]}
                      {...omit(getTagProps({index}), "key")}
                    />
                  );
                })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!error}
                  variant="outlined"
                  label={textFieldLabel}
                  placeholder={placeholder}
                />
              )}
              PaperComponent={(params) => <Paper square elevation={5} {...params} />}
            />
          </Grid>
          <FormGuide guide={guide} required={required} />
        </Grid>
      </span>
    </AgentFieldHighlight>
  );
};

CustomAutocomplete.propTypes = {
  allowExtended: PropTypes.bool,
  existingTags: PropTypes.array,
  guide: PropTypes.string,
  margin: PropTypes.object,
  name: PropTypes.string.isRequired,
  options: PropTypes.array,
  optionLabel: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.any,
  required: PropTypes.bool,
  selectAllValue: PropTypes.string,
  agentHighlight: PropTypes.bool,
  textFieldLabel: PropTypes.string,
  title: PropTypes.string,
};

export default CustomAutocomplete;
